import * as React from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qk } from '@client/lib/api';
import type { ActivityEvent, AdminGrant, AdminInvestor, AdminFirm } from '@client/lib/admin-api';
import { PageHeader, InvestorStatusBadge, Stat, EventChip } from '@client/components/admin/shared';
import { ActivityTable } from '@client/components/admin/activity-table';
import { Button } from '@client/components/ui/button';
import { ConfirmDialog } from '@client/components/ui/overlay';
import { Spinner } from '@client/components/ui/feedback';
import { Table, Td, Th, Tr } from '@client/components/ui/table';
import { useToast } from '@client/components/ui/toast';
import { fmtDate, fmtDateTime, fmtRelative } from '@client/lib/format';

export const Route = createFileRoute('/admin/investors/$id')({ component: InvestorPage });

interface Detail { investor: AdminInvestor; firm: AdminFirm | null; grants: Array<AdminGrant & { room_name: string; room_slug: string; room_status: string }>; sessions: Array<{ id: string; createdAt: string; lastSeenAt: string; ip: string | null; country: string | null; ua: string | null; previewStaffId: string | null }>; recent_events: ActivityEvent[]; engagement: number; rooms: Array<{ id: string; name: string; slug: string }> }

function InvestorPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const detail = useQuery({ queryKey: qk.admin('investor', id), queryFn: () => api<Detail>(`/api/admin/investors/${id}`) });
  const [confirm, setConfirm] = React.useState<'suspend' | 'remove' | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: qk.admin('investor', id) });
  const act = useMutation({
    mutationFn: (path: string) => api(path, { method: 'POST' }),
    onSuccess: () => { refresh(); toast.push({ tone: 'good', title: 'Done' }); },
    onError: (e) => toast.push({ tone: 'critical', title: 'Failed', body: String((e as Error).message) }),
  });
  if (detail.isPending) return <Spinner />;
  if (!detail.data) return null;
  const d = detail.data;
  const inv = d.investor;
  return (
    <>
      <PageHeader
        eyebrow={<Link to="/admin/investors" className="text-muted no-underline hover:text-foreground">Investors</Link>}
        title={<span className="flex items-center gap-3">{inv.name}<InvestorStatusBadge status={inv.status} /></span>}
        actions={
          <>
            {inv.status === 'suspended' ? <Button variant="secondary" onClick={() => act.mutate(`/api/admin/investors/${id}/reactivate`)}>Reactivate</Button> : null}
            {inv.status === 'active' || inv.status === 'invited' ? <Button variant="secondary" onClick={() => setConfirm('suspend')}>Suspend</Button> : null}
            {inv.status !== 'removed' ? <Button variant="destructive" onClick={() => setConfirm('remove')}>Remove (GDPR)</Button> : null}
          </>
        }
      />
      <div className="space-y-6 px-6 py-6 md:px-8">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Email" value={<span className="text-[15px] font-[520]">{inv.email}</span>} hint={inv.title ?? undefined} />
          <Stat label="Firm" value={<span className="text-[15px] font-[520]">{d.firm?.name ?? 'None'}</span>} hint={d.firm?.type} />
          <Stat label="Engagement 30d" value={d.engagement} />
          <Stat label="Last seen" value={<span className="text-[15px] font-[520]">{inv.lastSeenAt ? fmtRelative(inv.lastSeenAt, 'en') : 'never'}</span>} hint={`locale ${inv.locale}`} />
        </div>
        <section>
          <h2 className="section-title mb-3">Rooms</h2>
          <div className="rounded-card border border-border bg-card">
            <Table>
              <thead><tr><Th>Room</Th><Th>Via</Th><Th>Valid until</Th><Th /></tr></thead>
              <tbody>
                {d.grants.map((g) => (
                  <Tr key={g.id}>
                    <Td><Link to="/admin/rooms/$id" params={{ id: g.roomId }} className="font-[520]">{g.room_name}</Link><span className="ml-2 text-muted">{g.room_status}</span></Td>
                    <Td>{g.investorId ? 'person' : `firm`}</Td>
                    <Td>{g.revokedAt ? <span className="text-critical">revoked</span> : g.expiresAt ? fmtDate(g.expiresAt, 'en') : 'no end date'}</Td>
                    <Td className="text-right">{!g.revokedAt && g.room_status === 'open' ? <Button size="sm" variant="ghost" onClick={() => api(`/api/admin/investors/${id}/resend-invite`, { body: { room_id: g.roomId } }).then(() => toast.push({ tone: 'good', title: 'Invitation sent' }))}>Resend invite</Button> : null}</Td>
                  </Tr>
                ))}
                {d.grants.length === 0 ? <tr><Td colSpan={4} className="py-6 text-center text-muted">No room access.</Td></tr> : null}
              </tbody>
            </Table>
          </div>
        </section>
        <section>
          <h2 className="section-title mb-3">Active sessions</h2>
          <div className="rounded-card border border-border bg-card">
            <Table>
              <thead><tr><Th>Started</Th><Th>Last seen</Th><Th>Where</Th><Th>Browser</Th><Th /></tr></thead>
              <tbody>
                {d.sessions.map((s) => (
                  <Tr key={s.id}><Td className="text-muted">{fmtDateTime(s.createdAt, 'en')}</Td><Td className="text-muted">{fmtRelative(s.lastSeenAt, 'en')}</Td><Td>{s.country ?? ''} {s.ip ?? ''}{s.previewStaffId ? <span className="ml-1 text-[11px] text-warning">staff preview</span> : null}</Td><Td className="max-w-[280px] truncate text-muted" title={s.ua ?? ''}>{s.ua}</Td><Td className="text-right"><Button size="sm" variant="ghost" className="text-critical" onClick={() => act.mutate(`/api/admin/investors/${id}/sessions/${s.id}/revoke`)}>Revoke</Button></Td></Tr>
                ))}
                {d.sessions.length === 0 ? <tr><Td colSpan={5} className="py-6 text-center text-muted">No active sessions.</Td></tr> : null}
              </tbody>
            </Table>
          </div>
        </section>
        <section>
          <h2 className="section-title mb-3">Activity</h2>
          <ActivityTable fixed={{ actor_type: 'investor', actor_id: id }} compact />
        </section>
        {d.recent_events.length > 0 ? <p className="text-[12.5px] text-muted">Latest: <EventChip event={d.recent_events[0]!.event} /> {fmtRelative(d.recent_events[0]!.ts, 'en')}</p> : null}
      </div>
      <ConfirmDialog open={confirm === 'suspend'} onOpenChange={(o) => !o && setConfirm(null)} title="Suspend this investor?" description="All their sessions are revoked and every request is refused until reactivated." confirmLabel="Suspend" destructive onConfirm={() => { act.mutate(`/api/admin/investors/${id}/suspend`); setConfirm(null); }} />
      <ConfirmDialog open={confirm === 'remove'} onOpenChange={(o) => !o && setConfirm(null)} title="Remove and anonymise?" description="Name and email are replaced, grants revoked, sessions ended. The activity log keeps a pseudonymous id. This cannot be undone." confirmLabel="Remove" destructive onConfirm={() => { act.mutate(`/api/admin/investors/${id}/remove`); setConfirm(null); }} />
    </>
  );
}
